/**
 * RoleSelection Component
 * 
 * Handles user authentication via email/username and password.
 * Supports both email-based login (store owners) and username-based login (staff).
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onSelectRole - Callback when role is selected (legacy)
 * @param {Function} props.onAdminLogin - Callback for admin password login (legacy)
 * @param {string} props.adminPassword - Admin password for legacy auth
 * @param {Function} props.onUserLogin - Callback for user password login (legacy)
 * @param {string} props.userPassword - User password for legacy auth
 * @param {Function} props.onEmailLogin - Callback for email-based login
 * @param {Function} props.onBack - Callback to go back to previous screen
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

function RoleSelection({ onSelectRole, onAdminLogin, adminPassword, onUserLogin, userPassword, onEmailLogin, onBack }) {
  const [loginMode, setLoginMode] = useState('choice'); // 'choice', 'email', 'legacy'
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');
  const [loginType, setLoginType] = useState(''); // 'admin' or 'user'
  const [loading, setLoading] = useState(false);
  
  // Email login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleAdminClick = () => {
    setShowPasswordInput(true);
    setLoginType('admin');
    setError('');
    setLoginMode('legacy');
  };

  const handleUserClick = () => {
    setShowPasswordInput(true);
    setLoginType('user');
    setError('');
    setLoginMode('legacy');
  };

  // handleEmailLoginClick removed - login form now integrated into choice mode

  const handlePasswordSubmit = () => {
    const correctPassword = loginType === 'admin' ? adminPassword : userPassword;
    
    if (passwordInput === correctPassword) {
      if (loginType === 'admin') {
        onAdminLogin();
      } else {
        onUserLogin();
      }
      setShowPasswordInput(false);
      setPasswordInput('');
      setError('');
      setLoginType('');
    } else {
      setError('Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };
  
  const handleEmailLoginSubmit = async () => {
    if (!email || !password) {
      setError('Please enter both login and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const success = await onEmailLogin(email, password);
    
    if (!success) {
      setError('Invalid credentials');
    }
    
    setLoading(false);
  };

  const handleBack = () => {
    setLoginMode('choice');
    setShowPasswordInput(false);
    setPasswordInput('');
    setEmail('');
    setPassword('');
    setError('');
    setLoginType('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center relative">
            {onBack && (
              <button 
                onClick={onBack}
                className="absolute left-0 top-0 text-orange-600 hover:text-orange-700 flex items-center gap-1 text-sm sm:text-base"
                style={{minHeight: '44px', padding: '8px'}}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <img 
              src="https://customer-assets.emergentagent.com/job_bawarchi-catering-2/artifacts/6e6yv8t6_caterlog-high-resolution-logo-transparent.png" 
              alt="CaterLog" 
              className="h-14 sm:h-16 mx-auto mb-6"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loginMode === 'choice' && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-700">Sign In to Your Store</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">Enter your email (store owner) or username (staff)</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="label-bawarchi text-sm sm:text-base">Email or Username</label>
                  <input
                    id="email"
                    type="text"
                    className="input-bawarchi text-base"
                    style={{minHeight: '48px', fontSize: '16px'}}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com or username"
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="password" className="label-bawarchi text-sm sm:text-base">Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPasswordInput ? "text" : "password"}
                      className="input-bawarchi text-base pr-10"
                      style={{minHeight: '48px', fontSize: '16px'}}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEmailLoginSubmit()}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordInput(!showPasswordInput)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      style={{minHeight: '44px'}}
                    >
                      {showPasswordInput ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {error && <p className="error-message text-sm">{error}</p>}
                <button
                  onClick={handleEmailLoginSubmit}
                  className="btn-bawarchi-primary w-full text-base flex items-center justify-center gap-2"
                  style={{minHeight: '48px'}}
                  disabled={loading}
                >
                  {loading && (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </>
          )}
          {/* Email login mode removed - now integrated into choice mode */}

          {loginMode === 'legacy' && showPasswordInput && (
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="label-bawarchi">{loginType === 'admin' ? 'Admin' : 'User'} Password</label>
                <input
                  id="password"
                  type="password"
                  className="input-bawarchi"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder={`Enter ${loginType} password`}
                  autoFocus
                />
                {error && <p className="error-message">{error}</p>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePasswordSubmit}
                  className="btn-bawarchi-primary flex-1"
                >
                  Login
                </button>
                <button
                  onClick={handleBack}
                  className="btn-bawarchi-secondary flex-1"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default RoleSelection;
